const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return text; // Return the copied text on success
  } catch (error) {
    console.error('Error copying text:', error);
    throw error;
  }
};

const extractMetadata = (str) => {
  // Define metadata keywords to be extracted
  const metadataKeywords = [
    'Title', 'Article Headline', 'Word Count', 'Sources', 'Excerpt', 'Lead Text',
    'Similarity Score', 'Meta Title', 'Social Headline', 'Meta Description',
    'SEO Title', 'Headline', 'Newsletters Headline', 'Referrers Headline'
  ];

  // Generate regular expressions dynamically
  const metadataPatterns = metadataKeywords.map(keyword => ({
    keyword,
    pattern: new RegExp(`^(#{1,4} |\\*{1,4}\\s*)?${keyword}:\\s*"?([^"]*?)"?$`, 'im')
  }));

  // Special case for lines starting with ###
  const hashTitleRegex = /^###\s*(.*)$/;

  // Extract metadata using regular expressions
  const extractedMetadata = {};

  metadataPatterns.forEach(({ keyword, pattern }) => {
    const match = str.match(pattern);
    if (match) {
      extractedMetadata[keyword.replace(/\s+/g, '')] = match[2];
    }
  });

  // Handle the first line as a special case
  const lines = str.split('\n');
  const firstLine = lines[0].trim();
  const hashTitleMatch = firstLine.match(hashTitleRegex);

  // Extract title specifically from the first line if not already found
  if (hashTitleMatch && !extractedMetadata['Title']) {
    extractedMetadata['Title'] = hashTitleMatch[1];
  }

  // Custom logic to prioritise certain metadata fields
  const metadata = {
    title: extractedMetadata['ArticleHeadline'] || extractedMetadata['SEOHeadline'] || extractedMetadata['Headline'] || extractedMetadata['Title'] || '',
    leadText: extractedMetadata['LeadText'] || extractedMetadata['Excerpt'] || '',
    metaTitle: extractedMetadata['SocialHeadline'] || extractedMetadata['MetaTitle'] || '',
    metaDescription: extractedMetadata['MetaDescription'] || '',
    newsLettersAndReferrersHeadline: extractedMetadata['NewslettersHeadline'] || extractedMetadata['ReferrersHeadline'] || '',
  };

  return metadata;
};

const cleanContent = (str, cleanMetadata = false) => {
  // Define metadata keywords to be removed
  const metadataKeywords = [
    'Title', 'Word Count', 'Sources', 'Excerpt', 'Similarity Score',
    'Meta Title', 'Social Headline', 'Meta Description', 'Lead Text',
    'SEO Title', 'Headline', 'Newsletters Headline', 'Conclusion',
    'Reference', 'Published by', 'Dated', 'Date', 'Date Range', 'Time',
    'Published in Print Title', 'Director', 'Publication Reference', 'Publication', 'URL'
  ];

  // Generate metadata patterns dynamically
  const metadataPatterns = metadataKeywords.flatMap(keyword => [
    new RegExp(`^#\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^##\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^###\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^####\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^\\*\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^\\*\\*\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^\\*\\*\\*\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^\\*\\*\\*\\*\\s*${keyword}:?.*$`, 'm'),
    new RegExp(`^${keyword}:?.*$`, 'm')
  ]);

  // Check if the first line is a title and remove it if it matches the pattern
  const lines = str.split('\n');
  if (/^###\s/.test(lines[0])) {
    lines.shift();
  }

  let cleanedStr = lines.join('\n');

  // Remove each metadata pattern
  metadataPatterns.forEach(pattern => {
    cleanedStr = cleanedStr.replace(pattern, '');
  });

  // Remove hashes at the beginning and end of the string
  cleanedStr = cleanedStr.replace(/^(#\s*|##\s*|###\s*|####\s*)|(\s*#|##|###|####)$/g, '');

  // Remove asterisks at the beginning and end of the string
  cleanedStr = cleanedStr.replace(/^(^\*\s*|\*\*\s*|\*\*\*\s*|\*\*\*\*\s*)|(\s*\*|\*\*|\*\*\*|\*\*\*\*)$/g, '');

  // Remove double quotes at the beginning and end
  cleanedStr = cleanedStr.replace(/^"(.*)"$/, '$1');

  // Remove single quotes at the beginning and end
  cleanedStr = cleanedStr.replace(/^'(.*)'$/, '$1');

  // Remove ```markdown from the beginning and ``` from the end
  cleanedStr = cleanedStr.replace(/^```markdown\s*/, '');
  cleanedStr = cleanedStr.replace(/\s*```$/, '');

  if (cleanMetadata) {
    // Replace & with 'and'
    cleanedStr = cleanedStr.replace(/&/g, 'and');
    // Remove asterisks from the string
    cleanedStr = cleanedStr.replace(/\*/g, '');
    // Remove full stop at the end if present
    cleanedStr = cleanedStr.replace(/\.$/, '');
  }

  return cleanedStr.trim();
};

export const STANDARD_FIELDS = [
  "title",
  "leadText",
  "metaTitle",
  "metaDescription",
  "newsLettersAndReferrersHeadline",
  "tags",
  "body",
  "url",
  "wordCount",
  "sources",
];

// Field aliases for backward compatibility and snake_case/camelCase variants
export const KEY_MAP = {
  title: ["title"],
  leadText: ["leadText", "leadtext"],
  metaTitle: ["metaTitle", "metatitle"],
  metaDescription: ["metaDescription", "metadescription"],
  newsLettersAndReferrersHeadline: ["newsLettersAndReferrersHeadline", "newslettersheadline"],
  tags: ["tags"],
  body: ["body", "articleBody"],
  url: ["url", "originalNoticeUrl", "originalnotice_url"],
  wordCount: ["wordCount", "wordcount"],
  sources: ["sources"],
};

// Utility for flexible field resolution
export function getField(obj, key) {
  const variants = KEY_MAP[key] || [];
  for (const variant of variants) {
    const found = Object.keys(obj).find(k => k.toLowerCase() === variant.toLowerCase());
    if (found) return obj[found];
  }
  return "";
}

function extractJsonBlock(text) {
  if (typeof text !== 'string') return '';
  // Looks for the first code block in markdown format (``` or ```json), capturing everything inside (including newlines).
  // Pattern is case-insensitive and matches both plain and "json"-tagged code blocks.
  const match = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (match && match[1]) {
    const candidate = match[1].trim();
    if (candidate.startsWith('{') || candidate.startsWith('[')) {
      return candidate;
    }
  }
  return text; // fallback to original if no JSON block
}

function tryParseJson(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function isObjectOrArray(obj) {
  return typeof obj === 'object' && obj !== null;
}

function buildArticleFromJson(parsedJson) {
  const articleData = {};
  STANDARD_FIELDS.forEach(field => {
    articleData[field] = getField(parsedJson, field) ?? "";
  });
  return articleData;
}

function extractArticleData(message, responseFormat) {
  const rawContent = typeof message === 'string' ? message.trim() : '';

  // 1. Try parsing the entire message as JSON if it starts with { or [ or if responseFormat is json_object
  if (
    responseFormat === 'json_object' ||
    rawContent.startsWith('{') ||
    rawContent.startsWith('[')
  ) {
    const parsedWholeJson = tryParseJson(rawContent);
    if (parsedWholeJson && isObjectOrArray(parsedWholeJson)) {
      return buildArticleFromJson(parsedWholeJson);
    }
  }

  // 2. Try extracting a JSON block from markdown and parsing it
  const jsonFromMarkdown = extractJsonBlock(rawContent);
  if (jsonFromMarkdown && jsonFromMarkdown !== rawContent) {
    const parsedMarkdownJson = tryParseJson(jsonFromMarkdown);
    if (parsedMarkdownJson && isObjectOrArray(parsedMarkdownJson)) {
      return buildArticleFromJson(parsedMarkdownJson);
    }
  }

  // 3. Fallback: extract metadata and clean body
  const meta = extractMetadata(rawContent);

  return {
    title: meta.title || "",
    leadText: meta.leadText || "",
    metaTitle: meta.metaTitle || "",
    metaDescription: meta.metaDescription || "",
    newsLettersAndReferrersHeadline: meta.newsLettersAndReferrersHeadline || "",
    tags: Array.isArray(meta.tags)
      ? meta.tags
      : meta.tags
        ? meta.tags.split(',').map(tag => tag.trim())
        : [],
    body: cleanContent(rawContent),
    url: meta.url || "",
    wordCount: meta.wordCount || ""
  };
}

function normaliseAssistantMessages(messages, responseFormat) {
  let latestArticleData = null;

  // Pretty-print any JSON object (including arrays)
  function jsonToMarkdown(obj) {
    if (Array.isArray(obj)) {
      return obj.map(jsonToMarkdown).join('\n\n');
    }
    if (typeof obj !== "object" || obj === null) {
      return String(obj);
    }
    return Object.entries(obj)
      .map(([key, value]) => `**${key}:** ${typeof value === "object" ? `\n${jsonToMarkdown(value)}` : value}`)
      .join('\n\n');
  }

  const normalised = messages.map((msg) => {
    if (msg.role !== "assistant" || !msg.content) return msg;

    // Try to extract structured article data
    const article = extractArticleData(msg.content, responseFormat);

    // Check for presence of at least one editorial field (besides body)
    const hasOtherEditorialField = !!(
      (typeof article.title === "string" && article.title.trim().length > 0) ||
      (typeof article.leadText === "string" && article.leadText.trim().length > 0) ||
      (typeof article.metaTitle === "string" && article.metaTitle.trim().length > 0) ||
      (typeof article.metaDescription === "string" && article.metaDescription.trim().length > 0) ||
      (typeof article.newsLettersAndReferrersHeadline === "string" && article.newsLettersAndReferrersHeadline.trim().length > 0) ||
      (Array.isArray(article.tags) && article.tags.length > 0) ||
      (typeof article.url === "string" && article.url.trim().length > 0) ||
      (typeof article.wordCount === "number" && article.wordCount > 0) ||
      (Array.isArray(article.sources) && article.sources.length > 0)
    );

    const isEditorialArticle =
      article &&
      typeof article.body === "string" &&
      article.body.trim().length > 0 &&
      hasOtherEditorialField;

    // 1. Editorial article: show all fields, markdown formatting
    if (isEditorialArticle) {
      latestArticleData = article;
      const markdown = [
        article.title ? `# ${article.title}\n` : '',
        article.leadText ? `\n_${article.leadText}_\n` : '',
        article.body ? `\n${article.body.trim()}\n` : '',
        (
          (typeof article.metaTitle === "string" && article.metaTitle.trim().length > 0) ||
          (typeof article.metaDescription === "string" && article.metaDescription.trim().length > 0) ||
          (typeof article.newsLettersAndReferrersHeadline === "string" && article.newsLettersAndReferrersHeadline.trim().length > 0) ||
          (Array.isArray(article.tags) && article.tags.length > 0) ||
          (typeof article.url === "string" && article.url.trim().length > 0) ||
          (typeof article.wordCount === "number" && article.wordCount > 0) ||
          (Array.isArray(article.sources) && article.sources.length > 0)
        ) ? [
          `\n---\n`,
          `### Editorial Notes\n`,
          (typeof article.metaTitle === "string" && article.metaTitle.trim().length > 0) ? `- **Meta Title:** ${article.metaTitle}\n` : '',
          (typeof article.metaDescription === "string" && article.metaDescription.trim().length > 0) ? `- **Meta Description:** ${article.metaDescription}\n` : '',
          (typeof article.newsLettersAndReferrersHeadline === "string" && article.newsLettersAndReferrersHeadline.trim().length > 0) ? `- **Newsletter/Referrer Headline:** ${article.newsLettersAndReferrersHeadline}\n` : '',
          (Array.isArray(article.tags) && article.tags.length > 0) ? `- **Tags:** ${article.tags.join(', ')}\n` : '',
          (typeof article.url === "string" && article.url.trim().length > 0) ? `- **Primary Source URL:** [${article.url}](${article.url})\n` : '',
          (typeof article.wordCount === "number" && article.wordCount > 0) ? `- **Word Count:** ${article.wordCount}\n` : '',
          (Array.isArray(article.sources) && article.sources.length > 0)
            ? `- **Sources:**\n${article.sources.map(src => `  - [${src}](${src})`).join('\n')}\n`
            : '',
        ].join('') : ''
      ].join('').trim();

      return { ...msg, content: markdown };
    }

    // 2. JSON (responseFormat === "json_object") but not an article: pretty-print as markdown
    if (responseFormat === "json_object") {
      let obj = null;
      try {
        obj = typeof msg.content === "string" ? JSON.parse(msg.content) : msg.content;
      } catch { }
      if (obj && typeof obj === "object") {
        const markdown = jsonToMarkdown(obj);
        return { ...msg, content: markdown };
      }
    }

    // 3. Fallback: show the content as-is (plain)
    return msg;
  });

  return { messages: normalised, articleData: latestArticleData };
}

export {
  copyToClipboard,
  extractMetadata,
  cleanContent,
  extractArticleData,
  normaliseAssistantMessages
};
